import {useAuth} from './useAuth';
import {authFactory} from '../store/authFactory';
import {AuthorizationResult} from '../../../Services/authorization/AuthorizationService';
import {User} from '../models/User';

export function useAuthorization () {
  const {user, initialized: authInitialized} = useAuth();
  const {checkDashboardAccessUseCase, checkPermissionUseCase} = authFactory.getUseCases();

  const canAccessDashboard = (): AuthorizationResult => {
    return checkDashboardAccessUseCase.execute(user);
  };

  const hasPermission = (permission: string): boolean => {
    // Don't check permissions until auth is fully initialized
    if (!authInitialized || !user) {
      return false;
    }

    return checkPermissionUseCase.hasPermission(user, permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    // Don't check permissions until auth is fully initialized
    if (!authInitialized || !user) {
      return false;
    }

    return checkPermissionUseCase.hasAnyPermission(user, permissions);
  };

  const hasRole = (requiredRole: string): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.hasRole(user, requiredRole);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.hasAnyRole(user, roles);
  };

  const hasAllRoles = (roles: string[]): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.hasAllRoles(user, roles);
  };

  const canPerformAction = (action: string, resource: string): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.canPerformAction(user, action, resource);
  };

  const getRoleLevel = (): number => {
    if (!authInitialized || !user) {
      return 0;
    }
    return checkPermissionUseCase.getRoleLevel(user);
  };

  const canManageUser = (targetUser: User | null): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.canManageUser(user, targetUser);
  };

  const isAdmin = (): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.isAdmin(user);
  };

  const hasFinancialPermissions = (): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.hasFinancialPermissions(user);
  };

  const hasManagementPermissions = (): boolean => {
    if (!authInitialized || !user) {
      return false;
    }
    return checkPermissionUseCase.hasManagementPermissions(user);
  };

  return {
    canAccessDashboard,
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canPerformAction,
    getRoleLevel,
    canManageUser,
    isAdmin,
    hasFinancialPermissions,
    hasManagementPermissions,
    user
  };
}
